import { useState } from "react";
import { authPost, errorMessage, staleLinkMessage } from "../lib/authApi.ts";

// The reset-password landing. The mailed link arrives with `?token=`; a stale
// one arrives with `?error=`. See ADR-0042.

type ResetPasswordPageProps = {
  token: string | null;
  error: string | null;
};

/**
 * Sets a new password against the reset token in the URL, or explains why a
 * stale link did not work. A successful reset revokes every session the
 * person holds, so the page sends them to sign in. Rendered by the public
 * entry at `/reset-password`.
 */
export function ResetPasswordPage({ token, error }: ResetPasswordPageProps): JSX.Element {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "done">("idle");
  const [err, setErr] = useState<string | null>(null);

  if (error !== null || token === null) {
    return (
      <div className="auth-card">
        <h1>That link didn't work</h1>
        <div className="notice warn" role="alert">
          <div>{staleLinkMessage(error ?? "INVALID_TOKEN")}</div>
        </div>
        <div className="btn-row">
          <a className="btn primary lg block" href="/forgot-password">
            Ask for a new link
          </a>
        </div>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div className="auth-card">
        <h1>Password changed</h1>
        <p className="lede" role="status">
          You have been signed out everywhere. Sign in with your new password.
        </p>
        <div className="btn-row">
          <a className="btn primary lg block" href="/login">
            Sign in
          </a>
        </div>
      </div>
    );
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (password !== again) {
      setErr("Those passwords do not match.");
      return;
    }
    setState("saving");
    try {
      await authPost("/reset-password", { newPassword: password, token });
      setState("done");
    } catch (x) {
      setErr(errorMessage(x));
      setState("idle");
    }
  }

  return (
    <div className="auth-card">
      <form onSubmit={onSubmit} aria-label="Reset password">
        <h1>Set a new password</h1>
        <p className="lede">
          This link is good for fifteen minutes and can be used once. Resetting
          signs you out everywhere.
        </p>
        <div className="field">
          <label htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="reset-again">Confirm new password</label>
          <input
            id="reset-again"
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </div>
        {err ? (
          <div className="notice bad" role="alert">
            <div>{err}</div>
          </div>
        ) : null}
        <div className="btn-row">
          <button type="submit" className="btn primary lg block" disabled={state === "saving"}>
            {state === "saving" ? "Saving…" : "Set password"}
          </button>
        </div>
      </form>
    </div>
  );
}
